/**
 * SoleadLab 2.0 — Evidence Entity API Client
 *
 * B3.1: Frontend service for evidence item read operations.
 *
 * Evidence items are IMMUTABLE after creation (see evidence.ts handler).
 * This client exposes list + get only — no create/update/delete.
 * Backend version is always 1 (write-once); ETag returned for forward
 * compatibility.
 *
 * Follows the same per-service pattern as indicationApi.ts.
 */

import { apiFetch } from './apiFetch';

// ─── Types ──────────────────────────────────────────────────────────────────────

/** Raw row shape returned by the evidence entity API */
export interface EvidenceEntityRow {
  id: string;
  site_id: string;
  session_id: string | null;
  evidence_type: string;
  r2_key: string | null;
  original_name: string | null;
  content_type: string | null;
  file_size: number | null;
  capture_date: string | null;
  geo_json: string | null;
  metadata_json: string | null;
  asset_id: string | null;
  provenance_json: string | null;
  version: number;
  created_at: string;
  created_by: string | null;
}

/** Paginated list response */
interface EvidenceListResponse {
  items: EvidenceEntityRow[];
  total: number;
  page: number;
  limit: number;
  hasMore: boolean;
}

/** Frontend evidence shape (parsed JSON fields) */
export interface EvidenceItem {
  id: string;
  siteId: string;
  sessionId: string | null;
  evidenceType: string;
  r2Key: string | null;
  originalName: string | null;
  contentType: string | null;
  fileSize: number | null;
  captureDate: string | null;
  geo: { lng: number; lat: number } | null;
  metadata: Record<string, unknown> | null;
  assetId: string | null;
  provenance: Record<string, unknown> | null;
  version: number;
  createdAt: string;
  createdBy: string | null;
}

/** Result of fetching all evidence items for a site */
export interface EvidenceListResult {
  items: EvidenceItem[];
  total: number;
}

// ─── Transform ──────────────────────────────────────────────────────────────────

/** Entity row → frontend EvidenceItem shape */
function entityRowToEvidence(row: EvidenceEntityRow): EvidenceItem {
  let geo: { lng: number; lat: number } | null = null;
  if (row.geo_json) {
    try {
      const parsed = JSON.parse(row.geo_json);
      if (parsed.coordinates && Array.isArray(parsed.coordinates)) {
        geo = { lng: parsed.coordinates[0], lat: parsed.coordinates[1] };
      } else if (typeof parsed.lng === 'number' && typeof parsed.lat === 'number') {
        geo = { lng: parsed.lng, lat: parsed.lat };
      }
    } catch { /* invalid geo — leave null */ }
  }

  let metadata: Record<string, unknown> | null = null;
  if (row.metadata_json) {
    try { metadata = JSON.parse(row.metadata_json); } catch { /* ignore */ }
  }

  let provenance: Record<string, unknown> | null = null;
  if (row.provenance_json) {
    try { provenance = JSON.parse(row.provenance_json); } catch { /* ignore */ }
  }

  return {
    id: row.id,
    siteId: row.site_id,
    sessionId: row.session_id,
    evidenceType: row.evidence_type,
    r2Key: row.r2_key,
    originalName: row.original_name,
    contentType: row.content_type,
    fileSize: row.file_size,
    captureDate: row.capture_date,
    geo,
    metadata,
    assetId: row.asset_id,
    provenance,
    version: row.version,
    createdAt: row.created_at,
    createdBy: row.created_by,
  };
}

// ─── Read API ───────────────────────────────────────────────────────────────────

/**
 * Fetch ALL evidence items for a site from the entity API.
 * Paginates automatically (200 per page).
 */
export async function fetchEvidenceItems(
  siteId: string,
  typeFilter?: string,
): Promise<EvidenceListResult> {
  const allItems: EvidenceItem[] = [];
  let page = 1;
  let total = 0;
  const limit = 200;

  while (true) {
    let url = `/api/v2/sites/${siteId}/evidence?page=${page}&limit=${limit}`;
    if (typeFilter) url += `&type=${encodeURIComponent(typeFilter)}`;

    const result = await apiFetch<EvidenceListResponse>(url);

    if (page === 1) {
      total = result.total;
    }

    for (const row of result.items) {
      allItems.push(entityRowToEvidence(row));
    }

    if (!result.hasMore) break;
    page++;
  }

  return { items: allItems, total };
}

/**
 * Fetch a single evidence item by ID.
 * Returns null if not found (404).
 */
export async function fetchSingleEvidence(
  siteId: string,
  id: string,
): Promise<EvidenceItem | null> {
  try {
    const row = await apiFetch<EvidenceEntityRow>(
      `/api/v2/sites/${siteId}/evidence/${id}`,
    );
    return entityRowToEvidence(row);
  } catch {
    return null;
  }
}
